// Use environment variable or default to relative path for same-origin, or production URL
export const API_BASE = import.meta.env.VITE_API_URL ||
  (window.location.hostname === 'localhost' ? '/api/v1' : 'https://aistudio-api.wizice100.workers.dev/api/v1')

interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: {
    code: string
    message: string
    details?: Record<string, unknown>
  }
  meta?: {
    total: number
    limit: number
    offset: number
  }
}

// Generic fetch wrapper - 쿠키 기반 인증 사용
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const headers: HeadersInit = {
    ...options.headers,
  }

  // Add content-type for JSON bodies
  if (options.body && !(options.body instanceof FormData)) {
    (headers as Record<string, string>)['Content-Type'] = 'application/json'
  }

  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include', // HttpOnly 쿠키를 포함시킴
    })

    const data = await response.json()

    if (!response.ok) {
      // 401 Unauthorized - 세션 만료 또는 인증 실패
      if (response.status === 401) {
        // 로그인 페이지가 아닌 경우에만 리다이렉트
        if (!window.location.pathname.includes('/login') &&
            !window.location.pathname.includes('/auth/callback')) {
          // 로그인 페이지로 리다이렉트 (현재 페이지를 returnUrl로 저장)
          const returnUrl = encodeURIComponent(window.location.pathname + window.location.search)
          window.location.href = `/login?returnUrl=${returnUrl}`
        }
      }

      return {
        success: false,
        error: data.error || { code: 'UNKNOWN_ERROR', message: 'Unknown error' },
      }
    }

    return data
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : 'Network error',
      },
    }
  }
}

// ==================== Generate API ====================

export interface GenerationModel {
  id: string
  name: string
  cost: number
  features: string[]
  recommended?: boolean
}

export interface GenerationJob {
  generationId: string
  jobId: string
  accessKey: string
  status: 'processing' | 'completed' | 'failed'
  pointsUsed: number
  pointsRemaining: number
  imageUrl?: string
}

export interface GenerationStatus {
  jobId: string
  status: 'Processing' | 'Completed' | 'Failed'
  progress: number
  message: string
  model: string
  imageUrl?: string
  imageData?: string
  mimeType?: string
  processingTime?: number
  actualCost?: number
  errorMessage?: string
  errorCode?: string
}

export interface Text2ImgRequest {
  prompt: string
  negativePrompt?: string
  model?: string
  aspectRatio?: string
  style?: string
}

export interface Img2ImgRequest {
  image: File
  prompt?: string
  model?: string
}

export const generateApi = {
  // Get available models
  getModels: async (): Promise<ApiResponse<GenerationModel[]>> => {
    return apiFetch<GenerationModel[]>('/generate/models')
  },

  // Text to Image
  text2img: async (request: Text2ImgRequest): Promise<ApiResponse<GenerationJob>> => {
    return apiFetch<GenerationJob>('/generate/text2img', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  },

  // Image to Image
  img2img: async (request: Img2ImgRequest): Promise<ApiResponse<GenerationJob>> => {
    const formData = new FormData()
    formData.append('image', request.image)
    formData.append('options', JSON.stringify({
      prompt: request.prompt,
      model: request.model || 'gemini',
    }))

    return apiFetch<GenerationJob>('/generate/img2img', {
      method: 'POST',
      body: formData,
    })
  },

  // Check generation status
  checkStatus: async (jobId: string, accessKey: string): Promise<ApiResponse<GenerationStatus>> => {
    return apiFetch<GenerationStatus>(`/generate/status/${jobId}?accessKey=${encodeURIComponent(accessKey)}`)
  },

  // Alias for checkStatus
  getStatus: async (jobId: string, accessKey: string): Promise<ApiResponse<GenerationStatus>> => {
    return apiFetch<GenerationStatus>(`/generate/status/${jobId}?accessKey=${encodeURIComponent(accessKey)}`)
  },

  // Get generation history
  getHistory: async (limit = 20, offset = 0): Promise<ApiResponse<GenerationJob[]>> => {
    return apiFetch<GenerationJob[]>(`/generate/history?limit=${limit}&offset=${offset}`)
  },

  // Save generated image as asset
  saveAsAsset: async (data: {
    jobId: string
    accessKey: string
    name?: string
    type: string
    tags?: string
    description?: string
  }): Promise<ApiResponse<{ asset: Asset }>> => {
    return apiFetch<{ asset: Asset }>('/generate/save-as-asset', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
}

// ==================== Auth API ====================

export interface User {
  id: string
  email: string
  name: string
  avatarUrl?: string
  tier: string
  points: number
}

export interface AuthResult {
  // token과 refreshToken은 HttpOnly 쿠키로 전송됨
  user: User
}

export const authApi = {
  // Google OAuth
  loginGoogle: async (code: string, redirectUri?: string): Promise<ApiResponse<AuthResult>> => {
    return apiFetch<AuthResult>('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ code, redirectUri }),
    })
  },

  // Kakao OAuth
  loginKakao: async (code: string, redirectUri?: string): Promise<ApiResponse<AuthResult>> => {
    return apiFetch<AuthResult>('/auth/kakao', {
      method: 'POST',
      body: JSON.stringify({ code, redirectUri }),
    })
  },

  // Refresh token
  refresh: async (refreshToken: string): Promise<ApiResponse<{ token: string }>> => {
    return apiFetch<{ token: string }>('/auth/refresh', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${refreshToken}`,
      },
    })
  },

  // Logout
  logout: async (): Promise<ApiResponse<null>> => {
    return apiFetch<null>('/auth/logout', { method: 'POST' })
  },

  // Get login history (admin only)
  getLoginHistory: async (limit = 50, offset = 0): Promise<ApiResponse<LoginHistoryEntry[]>> => {
    return apiFetch<LoginHistoryEntry[]>(`/auth/login-history?limit=${limit}&offset=${offset}`)
  },
}

// Login history entry
export interface LoginHistoryEntry {
  id: string
  user_id: string
  email: string
  provider: string
  ip_address: string
  user_agent: string
  country: string | null
  city: string | null
  success: number
  error_message: string | null
  created_at: string
  user_name: string | null
  user_avatar: string | null
}

// ==================== Assets API ====================

export interface AssetKeyword {
  keyword: string
  keyword_type: 'analysis' | 'prompt' | 'genre' | 'style' | 'auto' | 'manual'
  element_type?: 'background' | 'character' | 'typography' | 'effect' | 'genre' | 'style' | 'all'
  source_field?: string
  confidence: number
  language: 'ko' | 'en' | 'mixed'
}

export interface Asset {
  id: string
  type: string
  name: string
  description?: string
  thumbnailUrl?: string
  imageUrl?: string
  isPublic: boolean
  isGenerated?: boolean
  generationPrompt?: string
  downloadCount: number
  viewCount: number
  createdAt: string
  tags?: { id: string; name: string; name_ko?: string }[]
  keywords?: AssetKeyword[]
  analysisId?: string | null
  // New fields for asset management
  isFavorited?: boolean
  userTags?: string[]
  isOwner?: boolean
  isDeleted?: boolean
  image_caption?: string | null
  deletedAt?: string
  deletedBy?: string
}

export const assetsApi = {
  // Get user's personal tags
  getUserTags: async (): Promise<ApiResponse<Array<{ name: string; count: number }>>> => {
    return apiFetch('/assets/user-tags')
  },

  // List assets
  list: async (options?: {
    type?: string
    public?: boolean
    mine?: boolean
    favorite?: boolean
    hidden?: boolean
    trash?: boolean
    userTag?: string
    limit?: number
    offset?: number
    sort?: string
    order?: string
  }): Promise<ApiResponse<Asset[]>> => {
    const params = new URLSearchParams()
    if (options?.type) params.set('type', options.type)
    if (options?.public !== undefined) params.set('public', String(options.public))
    if (options?.mine !== undefined) params.set('mine', String(options.mine))
    if (options?.favorite !== undefined) params.set('favorite', String(options.favorite))
    if (options?.hidden !== undefined) params.set('hidden', String(options.hidden))
    if (options?.trash !== undefined) params.set('trash', String(options.trash))
    if (options?.userTag) params.set('userTag', options.userTag)
    if (options?.limit) params.set('limit', String(options.limit))
    if (options?.offset) params.set('offset', String(options.offset))
    if (options?.sort) params.set('sort', options.sort)
    if (options?.order) params.set('order', options.order)

    return apiFetch<Asset[]>(`/assets?${params.toString()}`)
  },

  // Get single asset
  get: async (id: string): Promise<ApiResponse<Asset>> => {
    return apiFetch<Asset>(`/assets/${id}`)
  },

  // Upload asset
  upload: async (file: File, metadata: {
    type: string
    name: string
    description?: string
    isPublic?: boolean
    tags?: string[]
  }): Promise<ApiResponse<Asset>> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('metadata', JSON.stringify(metadata))

    return apiFetch<Asset>('/assets', {
      method: 'POST',
      body: formData,
    })
  },

  // Update asset
  update: async (id: string, data: {
    name?: string
    description?: string
    isPublic?: boolean
  }): Promise<ApiResponse<Asset>> => {
    return apiFetch<Asset>(`/assets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  // Delete asset
  delete: async (id: string): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/assets/${id}`, { method: 'DELETE' })
  },

  // Add to favorites
  addFavorite: async (id: string): Promise<ApiResponse<{ favorited: boolean }>> => {
    return apiFetch<{ favorited: boolean }>(`/assets/${id}/favorite`, { method: 'POST' })
  },

  // Remove from favorites
  removeFavorite: async (id: string): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/assets/${id}/favorite`, { method: 'DELETE' })
  },

  // Hide asset
  hideAsset: async (id: string): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/assets/${id}/hide`, { method: 'POST' })
  },

  // Unhide asset
  unhideAsset: async (id: string): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/assets/${id}/hide`, { method: 'DELETE' })
  },

  // Add user tag
  addUserTag: async (id: string, tagName: string): Promise<ApiResponse<{
    id: string
    user_id: string
    asset_id: string
    tag_name: string
    created_at: string
  }>> => {
    return apiFetch(`/assets/${id}/tags`, {
      method: 'POST',
      body: JSON.stringify({ tagName }),
    })
  },

  // Remove user tag
  removeUserTag: async (id: string, tagName: string): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/assets/${id}/tags/${encodeURIComponent(tagName)}`, {
      method: 'DELETE',
    })
  },

  // Add system tag
  addSystemTag: async (id: string, tagName: string): Promise<ApiResponse<{
    tag_id: string
    tag_name: string
    asset_id: string
  }>> => {
    return apiFetch(`/assets/${id}/system-tags`, {
      method: 'POST',
      body: JSON.stringify({ tagName }),
    })
  },

  // Remove system tag
  removeSystemTag: async (id: string, tagName: string): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/assets/${id}/system-tags/${encodeURIComponent(tagName)}`, {
      method: 'DELETE',
    })
  },

  // Restore deleted asset
  restoreAsset: async (id: string): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/assets/${id}/restore`, { method: 'POST' })
  },

  // Legacy: Toggle favorite (for backward compatibility)
  toggleFavorite: async (id: string): Promise<ApiResponse<{ favorited: boolean }>> => {
    return apiFetch<{ favorited: boolean }>(`/assets/${id}/favorite`, { method: 'POST' })
  },

  // Keyword-based search
  search: async (query: string, options?: {
    limit?: number
    offset?: number
  }): Promise<ApiResponse<Asset[]> & { meta?: { extractedKeywords?: string[]; matchCount?: number } }> => {
    return apiFetch<Asset[]>('/assets/search', {
      method: 'POST',
      body: JSON.stringify({
        query,
        limit: options?.limit || 20,
        offset: options?.offset || 0,
      }),
    })
  },
}

// ==================== Users API ====================

export const usersApi = {
  // Get current user profile
  getProfile: async (): Promise<ApiResponse<User>> => {
    return apiFetch<User>('/users/me')
  },

  // Update profile
  updateProfile: async (updates: { name?: string }): Promise<ApiResponse<User>> => {
    return apiFetch<User>('/users/me', {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
  },
}

// ==================== Points API ====================

export interface PointHistory {
  id: string
  amount: number
  type: string
  reason: string
  balance_after: number
  created_at: string
}

export const pointsApi = {
  // Get balance and history
  getBalanceAndHistory: async (limit = 20, offset = 0): Promise<ApiResponse<{ balance: number; history: PointHistory[] }>> => {
    return apiFetch<{ balance: number; history: PointHistory[] }>(`/points?limit=${limit}&offset=${offset}`)
  },

  // Get balance (alias)
  getBalance: async (): Promise<ApiResponse<{ balance: number; history: PointHistory[] }>> => {
    return apiFetch<{ balance: number; history: PointHistory[] }>('/points?limit=0')
  },

  // Get history (convenience)
  getHistory: async (limit = 20, offset = 0): Promise<ApiResponse<{ balance: number; history: PointHistory[] }>> => {
    return apiFetch<{ balance: number; history: PointHistory[] }>(`/points?limit=${limit}&offset=${offset}`)
  },
}

// ==================== Search API ====================

export interface SimilarAsset extends Asset {
  similarity: number
  visualScore?: number
  semanticScore?: number
}

export interface SearchStats {
  vectorCount: number
  dimensions: number
  metric?: string
}

export interface PopularTag {
  id: string
  name: string
  name_ko?: string
  category?: string
  usage_count: number
}

export interface AutocompleteResult {
  tags: PopularTag[]
  assets: { id: string; name: string; type: string }[]
}

export const searchApi = {
  // Hybrid search (3-tier: Tag → Keyword → Vector)
  hybrid: async (options: {
    q: string
    type?: string
    limit?: number
    offset?: number
  }): Promise<ApiResponse<Asset[]>> => {
    const params = new URLSearchParams()
    params.set('q', options.q)
    if (options.type) params.set('type', options.type)
    if (options.limit) params.set('limit', String(options.limit))
    if (options.offset) params.set('offset', String(options.offset))

    return apiFetch<Asset[]>(`/search/hybrid?${params.toString()}`)
  },

  // Text search (legacy - LIKE based)
  text: async (options: {
    q: string
    type?: string
    limit?: number
    offset?: number
  }): Promise<ApiResponse<Asset[]>> => {
    const params = new URLSearchParams()
    params.set('q', options.q)
    if (options.type) params.set('type', options.type)
    if (options.limit) params.set('limit', String(options.limit))
    if (options.offset) params.set('offset', String(options.offset))

    return apiFetch<Asset[]>(`/search/text?${params.toString()}`)
  },

  // Tag search
  tags: async (options: {
    tags: string[]
    type?: string
    limit?: number
    offset?: number
  }): Promise<ApiResponse<Asset[]>> => {
    const params = new URLSearchParams()
    params.set('tags', options.tags.join(','))
    if (options.type) params.set('type', options.type)
    if (options.limit) params.set('limit', String(options.limit))
    if (options.offset) params.set('offset', String(options.offset))

    return apiFetch<Asset[]>(`/search/tags?${params.toString()}`)
  },

  // Similar image search by asset ID
  similar: async (assetId: string, limit = 20, mode: 'hybrid' | 'visual' | 'semantic' = 'hybrid'): Promise<ApiResponse<SimilarAsset[]>> => {
    return apiFetch<SimilarAsset[]>(`/search/similar/${assetId}?limit=${limit}&mode=${mode}`)
  },

  // Popular tags
  popularTags: async (category?: string, limit = 20): Promise<ApiResponse<PopularTag[]>> => {
    const params = new URLSearchParams()
    if (category) params.set('category', category)
    params.set('limit', String(limit))

    return apiFetch<PopularTag[]>(`/search/popular-tags?${params.toString()}`)
  },

  // Autocomplete
  autocomplete: async (q: string): Promise<ApiResponse<AutocompleteResult>> => {
    return apiFetch<AutocompleteResult>(`/search/autocomplete?q=${encodeURIComponent(q)}`)
  },

  // Get Vectorize stats
  stats: async (): Promise<ApiResponse<SearchStats>> => {
    return apiFetch<SearchStats>('/search/stats')
  },
}

// ==================== Palettes API ====================

export interface Palette {
  id: string
  user_id: string
  cover_id?: string
  name: string
  description?: string
  is_default: boolean
  created_at: string
  updated_at: string
  assetCount?: number
  assets?: (Asset & {
    order_index: number
    note?: string
    added_at: string
  })[]
}

export const palettesApi = {
  // List palettes
  list: async (options?: {
    coverId?: string
    type?: 'shared'
    limit?: number
    offset?: number
  }): Promise<ApiResponse<Palette[]>> => {
    const params = new URLSearchParams()
    if (options?.coverId) params.set('coverId', options.coverId)
    if (options?.type) params.set('type', options.type)
    if (options?.limit) params.set('limit', String(options.limit))
    if (options?.offset) params.set('offset', String(options.offset))

    return apiFetch<Palette[]>(`/palettes?${params.toString()}`)
  },

  // Get palette with assets
  get: async (id: string): Promise<ApiResponse<Palette>> => {
    return apiFetch<Palette>(`/palettes/${id}`)
  },

  // Create palette
  create: async (data: {
    name: string
    description?: string
    coverId?: string
  }): Promise<ApiResponse<Palette>> => {
    return apiFetch<Palette>('/palettes', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Update palette
  update: async (id: string, data: {
    name?: string
    description?: string
  }): Promise<ApiResponse<Palette>> => {
    return apiFetch<Palette>(`/palettes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  // Delete palette
  delete: async (id: string): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/palettes/${id}`, {
      method: 'DELETE',
    })
  },

  // Add assets to palette
  addAssets: async (id: string, assetIds: string[]): Promise<ApiResponse<{ added: number }>> => {
    return apiFetch<{ added: number }>(`/palettes/${id}/assets`, {
      method: 'POST',
      body: JSON.stringify({ assetIds }),
    })
  },

  // Remove asset from palette
  removeAsset: async (id: string, assetId: string): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/palettes/${id}/assets/${assetId}`, {
      method: 'DELETE',
    })
  },

  // Reorder assets
  reorderAssets: async (id: string, assetIds: string[]): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/palettes/${id}/assets/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ assetIds }),
    })
  },
}

// ==================== Covers API ====================

export interface Cover {
  id: string
  user_id: string
  title: string
  width: number
  height: number
  data: {
    version: string
    canvas: {
      width: number
      height: number
      backgroundColor?: string
    }
    layers: unknown[]
    mainAssetId?: string  // 메인 영역 asset ID (썸네일 생성용)
  }
  is_public: boolean
  is_template: boolean
  parent_id?: string
  thumbnail_key?: string
  thumbnailUrl?: string
  fork_count: number
  created_at: string
  updated_at: string
}

export const coversApi = {
  // List covers
  list: async (options?: {
    template?: boolean
    public?: boolean
    mine?: boolean
    limit?: number
    offset?: number
  }): Promise<ApiResponse<Cover[]>> => {
    const params = new URLSearchParams()
    if (options?.template !== undefined) params.set('template', String(options.template))
    if (options?.public !== undefined) params.set('public', String(options.public))
    if (options?.mine !== undefined) params.set('mine', String(options.mine))
    if (options?.limit) params.set('limit', String(options.limit))
    if (options?.offset) params.set('offset', String(options.offset))

    return apiFetch<Cover[]>(`/covers?${params.toString()}`)
  },

  // Get cover
  get: async (id: string): Promise<ApiResponse<Cover>> => {
    return apiFetch<Cover>(`/covers/${id}`)
  },

  // Create cover
  create: async (data: {
    title: string
    width: number
    height: number
    backgroundColor?: string
    data: Cover['data']
    parentId?: string
  }): Promise<ApiResponse<Cover>> => {
    return apiFetch<Cover>('/covers', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Update cover
  update: async (id: string, data: {
    title?: string
    data?: Cover['data']
    isPublic?: boolean
    isTemplate?: boolean
  }): Promise<ApiResponse<Cover>> => {
    return apiFetch<Cover>(`/covers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  // Delete cover
  delete: async (id: string): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/covers/${id}`, {
      method: 'DELETE',
    })
  },

  // Duplicate cover
  duplicate: async (id: string): Promise<ApiResponse<Cover>> => {
    return apiFetch<Cover>(`/covers/${id}/duplicate`, {
      method: 'POST',
    })
  },

  // Get cover's default palette
  getPalette: async (id: string): Promise<ApiResponse<Palette>> => {
    return apiFetch<Palette>(`/covers/${id}/palette`)
  },

  // AI Generation: Generate prompt from selected assets
  generatePrompt: async (id: string, data: {
    assetIds: string[]
    coverWidth: number
    coverHeight: number
    customInstructions?: string
  }): Promise<ApiResponse<{
    prompt: string
    assetCount: number
    aspectRatio: string
  }>> => {
    return apiFetch(`/covers/${id}/ai/generate-prompt`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  },

  // AI Generation: Generate cover image
  generateCover: async (id: string, data: {
    model?: string
    prompt: string
    referenceAssetIds?: string[]
    aspectRatio?: string
  }): Promise<ApiResponse<{
    assetId: string
    imageUrl: string
    thumbnailUrl: string
    cost: number
    pointsRemaining: number
  }>> => {
    return apiFetch(`/covers/${id}/ai/generate`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  },
}

// ==================== Analyzer API ====================

export interface PromptTemplate {
  id: string
  user_id?: string
  name: string
  description?: string
  element_type: 'background' | 'character' | 'typography' | 'effect' | 'all'
  template_text: string
  is_system: boolean
  is_public: boolean
  usage_count: number
  created_at: string
  updated_at: string
}

export interface ElementAnalysis {
  description?: string
  setting?: string
  mood?: string
  style?: string
  prompt?: string
  colors?: string | string[]
  composition?: string
  lighting?: string
  [key: string]: unknown
}

export interface ImageAnalysis {
  id: string
  user_id: string
  asset_id?: string
  source_image_url?: string
  source_image_key?: string
  template_id?: string
  status: 'pending' | 'processing' | 'completed' | 'failed'

  // Analysis results (6개 요소)
  background_analysis?: string
  character_analysis?: string
  typography_analysis?: string
  subtitle_analysis?: string
  author_analysis?: string
  publisher_analysis?: string
  effect_analysis?: string

  backgroundAnalysis?: ElementAnalysis
  characterAnalysis?: ElementAnalysis
  typographyAnalysis?: ElementAnalysis
  subtitleAnalysis?: ElementAnalysis
  authorAnalysis?: ElementAnalysis
  publisherAnalysis?: ElementAnalysis
  effectAnalysis?: ElementAnalysis

  // Prompts (6개 요소)
  background_prompt?: string
  character_prompt?: string
  typography_prompt?: string
  subtitle_prompt?: string
  author_prompt?: string
  publisher_prompt?: string
  effect_prompt?: string

  // Variations (6개 요소 + global)
  global_variation: number
  background_variation: number
  character_variation: number
  typography_variation: number
  subtitle_variation: number
  author_variation: number
  publisher_variation: number
  effect_variation: number

  // Final prompts
  final_background_prompt?: string
  final_character_prompt?: string
  final_typography_prompt?: string
  final_subtitle_prompt?: string
  final_author_prompt?: string
  final_publisher_prompt?: string
  final_effect_prompt?: string

  // Generated assets
  generated_background_id?: string
  generated_character_id?: string
  generated_typography_id?: string
  generated_subtitle_id?: string
  generated_author_id?: string
  generated_publisher_id?: string
  generated_effect_id?: string

  // Generated models (각 요소별 사용한 이미지 생성 모델)
  generated_background_model?: string
  generated_character_model?: string
  generated_typography_model?: string
  generated_subtitle_model?: string
  generated_author_model?: string
  generated_publisher_model?: string
  generated_effect_model?: string

  // Integrated cover (통합 표지)
  integrated_cover_asset_id?: string
  integrated_cover_model?: string
  integrated_cover_prompt?: string
  integrated_cover_elements?: string // JSON array of element types
  integrated_cover_cost?: number
  integrated_cover_generated_at?: string

  model_used: string
  processing_time_ms?: number
  error_message?: string
  created_at: string
  updated_at: string
}

// 저장된 프롬프트
export interface SavedPrompt {
  id: string
  user_id: string
  name: string
  description?: string
  analysis_id?: string
  background_prompt?: string
  character_prompt?: string
  title_prompt?: string
  subtitle_prompt?: string
  author_prompt?: string
  publisher_prompt?: string
  background_variation: number
  character_variation: number
  title_variation: number
  subtitle_variation: number
  author_variation: number
  publisher_variation: number
  global_variation: number
  model?: string
  aspect_ratio?: string
  use_image_to_image: boolean
  created_at: string
  updated_at: string
}

export interface RegenerateResult {
  assetId: string
  elementType: string
  imageUrl: string
  thumbnailUrl: string
  pointsUsed: number
  pointsRemaining: number
}

export const analyzerApi = {
  // ===== Templates =====

  // List templates
  listTemplates: async (options?: {
    type?: string
    system?: boolean
  }): Promise<ApiResponse<PromptTemplate[]>> => {
    const params = new URLSearchParams()
    if (options?.type) params.set('type', options.type)
    if (options?.system !== undefined) params.set('system', String(options.system))

    return apiFetch<PromptTemplate[]>(`/analyzer/templates?${params.toString()}`)
  },

  // Get template
  getTemplate: async (id: string): Promise<ApiResponse<PromptTemplate>> => {
    return apiFetch<PromptTemplate>(`/analyzer/templates/${id}`)
  },

  // Create template
  createTemplate: async (data: {
    name: string
    description?: string
    elementType: 'background' | 'character' | 'typography' | 'effect' | 'all'
    templateText: string
    isPublic?: boolean
  }): Promise<ApiResponse<PromptTemplate>> => {
    return apiFetch<PromptTemplate>('/analyzer/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Update template
  updateTemplate: async (id: string, data: {
    name?: string
    description?: string
    templateText?: string
    isPublic?: boolean
  }): Promise<ApiResponse<PromptTemplate>> => {
    return apiFetch<PromptTemplate>(`/analyzer/templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  // Delete template
  deleteTemplate: async (id: string): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/analyzer/templates/${id}`, { method: 'DELETE' })
  },

  // ===== Analysis =====

  // Analyze image from asset
  analyzeAsset: async (assetId: string, options?: {
    templateId?: string
    elementTypes?: ('background' | 'character' | 'typography' | 'effect')[]
    model?: string
  }): Promise<ApiResponse<ImageAnalysis>> => {
    return apiFetch<ImageAnalysis>('/analyzer/analyze', {
      method: 'POST',
      body: JSON.stringify({
        assetId,
        templateId: options?.templateId,
        elementTypes: options?.elementTypes,
        model: options?.model,
      }),
    })
  },

  // Analyze uploaded image
  analyzeImage: async (file: File, options?: {
    templateId?: string
    elementTypes?: ('background' | 'character' | 'typography' | 'effect')[]
    model?: string
  }): Promise<ApiResponse<ImageAnalysis>> => {
    const formData = new FormData()
    formData.append('image', file)
    if (options?.templateId) formData.append('templateId', options.templateId)
    if (options?.elementTypes) formData.append('elementTypes', JSON.stringify(options.elementTypes))
    if (options?.model) formData.append('model', options.model)

    return apiFetch<ImageAnalysis>('/analyzer/analyze', {
      method: 'POST',
      body: formData,
    })
  },

  // Get analysis result
  getAnalysis: async (id: string): Promise<ApiResponse<ImageAnalysis>> => {
    return apiFetch<ImageAnalysis>(`/analyzer/analyses/${id}`)
  },

  // List analysis history
  listAnalyses: async (options?: {
    limit?: number
    offset?: number
    assetId?: string // 특정 에셋의 분석만 필터링
  }): Promise<ApiResponse<ImageAnalysis[]>> => {
    const params = new URLSearchParams()
    if (options?.limit) params.set('limit', String(options.limit))
    if (options?.offset) params.set('offset', String(options.offset))
    if (options?.assetId) params.set('assetId', options.assetId)

    return apiFetch<ImageAnalysis[]>(`/analyzer/analyses?${params.toString()}`)
  },

  // Reanalyze specific element
  reanalyzeElement: async (analysisId: string, elementType: 'background' | 'character' | 'title' | 'subtitle' | 'author' | 'publisher', data: {
    templateId?: string
    customPrompt?: string
    model?: string
  }): Promise<ApiResponse<ImageAnalysis>> => {
    return apiFetch<ImageAnalysis>(`/analyzer/analyses/${analysisId}/element/${elementType}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  // Apply variation to analysis
  applyVariation: async (analysisId: string, data: {
    backgroundVariation?: number
    characterVariation?: number
    typographyVariation?: number
    effectVariation?: number
    customPrompts?: {
      background?: string
      character?: string
      typography?: string
      effect?: string
    }
  }): Promise<ApiResponse<ImageAnalysis>> => {
    return apiFetch<ImageAnalysis>(`/analyzer/analyses/${analysisId}/variation`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  // Regenerate element (6개 요소)
  regenerate: async (analysisId: string, data: {
    elementType: 'background' | 'character' | 'title' | 'subtitle' | 'author' | 'publisher'
    prompt?: string
    model?: string
    aspectRatio?: string
    useImageToImage?: boolean
    referenceImageUrl?: string
    backgroundColor?: string
  }): Promise<ApiResponse<RegenerateResult>> => {
    return apiFetch<RegenerateResult>(`/analyzer/analyses/${analysisId}/regenerate`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // ===== Saved Prompts =====

  // Save prompt
  savePrompt: async (data: {
    name: string
    description?: string
    analysisId?: string
    prompts?: {
      background?: string
      character?: string
      title?: string
      subtitle?: string
      author?: string
      publisher?: string
    }
    variations?: {
      background?: number
      character?: number
      title?: number
      subtitle?: number
      author?: number
      publisher?: number
      global?: number
    }
    settings?: {
      model?: string
      aspectRatio?: string
      useImageToImage?: boolean
    }
  }): Promise<ApiResponse<SavedPrompt>> => {
    return apiFetch<SavedPrompt>('/analyzer/saved-prompts', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // List saved prompts
  listSavedPrompts: async (options?: {
    limit?: number
    offset?: number
  }): Promise<ApiResponse<SavedPrompt[]>> => {
    const params = new URLSearchParams()
    if (options?.limit) params.set('limit', String(options.limit))
    if (options?.offset) params.set('offset', String(options.offset))

    return apiFetch<SavedPrompt[]>(`/analyzer/saved-prompts?${params.toString()}`)
  },

  // Get saved prompt
  getSavedPrompt: async (id: string): Promise<ApiResponse<SavedPrompt>> => {
    return apiFetch<SavedPrompt>(`/analyzer/saved-prompts/${id}`)
  },

  // Update saved prompt
  updateSavedPrompt: async (id: string, data: {
    name?: string
    description?: string
    prompts?: {
      background?: string
      character?: string
      title?: string
      subtitle?: string
      author?: string
      publisher?: string
    }
    variations?: {
      background?: number
      character?: number
      title?: number
      subtitle?: number
      author?: number
      publisher?: number
      global?: number
    }
    settings?: {
      model?: string
      aspectRatio?: string
      useImageToImage?: boolean
    }
  }): Promise<ApiResponse<SavedPrompt>> => {
    return apiFetch<SavedPrompt>(`/analyzer/saved-prompts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  // Delete saved prompt
  deleteSavedPrompt: async (id: string): Promise<ApiResponse<null>> => {
    return apiFetch<null>(`/analyzer/saved-prompts/${id}`, { method: 'DELETE' })
  },

  // ===== Text Transform =====

  // Get available transform models
  getTransformModels: async (): Promise<ApiResponse<Array<{
    id: string
    name: string
    provider: string
    description: string
  }>>> => {
    return apiFetch('/analyzer/transform-models')
  },

  // Transform text using AI (세부요소별 변형)
  transformText: async (data: {
    text: string
    variation: number
    field: string
    elementType: string
    model?: string  // 모델 ID (text-to-text API 모델)
  }): Promise<ApiResponse<{ transformedText: string; model: string }>> => {
    return apiFetch<{ transformedText: string; model: string }>('/analyzer/transform-text', {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        model: data.model || 'claude-sonnet-4-20250514'  // 기본값: Claude Sonnet 4
      }),
    })
  },

  // ===== Vectorize & Asset Registration =====

  // Register element as asset with automatic vectorization
  registerElementAsAsset: async (
    analysisId: string,
    data: {
      element: 'background' | 'character' | 'title' | 'subtitle' | 'author' | 'publisher'
      imageUrl: string
      prompt: string
      assetMetadata: {
        name: string
        description?: string
        tags?: string[]
        isPublic?: boolean
      }
    }
  ): Promise<ApiResponse<{
    asset: unknown
    vectorId: string | null
    vectorizationStatus: 'success' | 'failed' | 'skipped'
    vectorError?: string
  }>> => {
    return apiFetch(`/analyzer/analyses/${analysisId}/register-element`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Search elements by vector similarity
  searchElements: async (params: {
    query: string
    type?: 'background' | 'character' | 'title' | 'subtitle' | 'author' | 'publisher'
    limit?: number
  }): Promise<ApiResponse<Array<{
    id: string
    score: number
    type: string
    prompt: string
    imageUrl?: string
    assetId?: string
    createdAt: number
  }>>> => {
    const searchParams = new URLSearchParams()
    searchParams.append('query', params.query)
    if (params.type) searchParams.append('type', params.type)
    if (params.limit) searchParams.append('limit', params.limit.toString())

    return apiFetch(`/search/elements?${searchParams.toString()}`)
  },

  // Vectorize element (manual)
  vectorizeElement: async (
    analysisId: string,
    element: 'background' | 'character' | 'title' | 'subtitle' | 'author' | 'publisher',
    data: {
      imageUrl?: string
      prompt: string
    }
  ): Promise<ApiResponse<{
    vectorId: string
    dimensions: number
  }>> => {
    return apiFetch(`/vectorize/analyses/${analysisId}/elements/${element}`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Generate integrated cover from multiple elements
  generateIntegratedCover: async (
    analysisId: string,
    data: {
      elementTypes: ('background' | 'character' | 'title' | 'subtitle' | 'effect' | 'original')[]
      customPrompt?: string
      model?: string
    }
  ): Promise<ApiResponse<{
    generationId: string
    jobId: string
    status: 'processing'
    elementTypes: string[]
    estimatedCost: number
  }>> => {
    return apiFetch(`/analyzer/${analysisId}/generate-integrated-cover`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Generate composite cover from multiple element images (image-to-image)
  generateCompositeCover: async (
    analysisId: string,
    data: {
      elementTypes: ('background' | 'character' | 'title' | 'subtitle' | 'effect' | 'original')[]
      compositionPrompt?: string
      model?: string
    }
  ): Promise<ApiResponse<{
    generationId: string
    jobId: string
    status: 'processing'
    elementTypes: string[]
    estimatedCost: number
  }>> => {
    return apiFetch(`/analyzer/${analysisId}/generate-composite-cover`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Check cover generation status
  getCoverGenerationStatus: async (
    analysisId: string,
    generationId: string
  ): Promise<ApiResponse<{
    status: 'processing' | 'completed' | 'failed'
    progress?: number
    assetId?: string
    imageUrl?: string
    thumbnailUrl?: string
    cost?: number
    pointsRemaining?: number
    errorMessage?: string
  }>> => {
    return apiFetch(`/analyzer/${analysisId}/cover-generation-status/${generationId}`, {
      method: 'GET',
    })
  },
}

// ==================== PRICING API ====================

export interface ModelPricing {
  id: number
  model_id: string
  category: string
  display_name: string
  provider: string
  input_cost_per_1m: number
  output_cost_per_1m: number
  per_request_cost: number
  margin_type: string
  margin_value: number
  final_points: number
  is_manual_override: number
  is_active: number
  created_at: string
  updated_at: string
}

export interface SystemSetting {
  key: string
  value: string
  description: string | null
  updated_at: string
  updated_by: string | null
}

export interface PricingHistoryEntry {
  id: number
  model_id: string
  changed_by: string
  changed_by_name: string | null
  changed_by_email: string | null
  field_name: string
  old_value: string
  new_value: string
  created_at: string
}

export const pricingApi = {
  list: async () =>
    apiFetch<Array<{ model_id: string; category: string; display_name: string; provider: string; final_points: number }>>('/pricing'),
  listAll: async () =>
    apiFetch<ModelPricing[]>('/pricing/admin'),
  update: async (modelId: string, data: Partial<ModelPricing>) =>
    apiFetch<ModelPricing>(`/pricing/admin/${modelId}`, { method: 'PUT', body: JSON.stringify(data) }),
  create: async (data: Partial<ModelPricing>) =>
    apiFetch<ModelPricing>('/pricing/admin', { method: 'POST', body: JSON.stringify(data) }),
  delete: async (modelId: string) =>
    apiFetch('/pricing/admin/' + modelId, { method: 'DELETE' }),
  getSettings: async () =>
    apiFetch<SystemSetting[]>('/pricing/admin/settings'),
  updateSetting: async (key: string, value: string, description?: string) =>
    apiFetch<SystemSetting>(`/pricing/admin/settings/${key}`, { method: 'PUT', body: JSON.stringify({ value, description }) }),
  getHistory: async (limit = 50, offset = 0) =>
    apiFetch<PricingHistoryEntry[]>(`/pricing/admin/history?limit=${limit}&offset=${offset}`),
}

// ==================== DASHBOARD API ====================

export const dashboardApi = {
  stats: async () => apiFetch<{
    weekGenerations: number
    editRate: number
    activeProjects: number
    totalGenerations: number
    channelStats: Array<{ channel_name: string; count: number }>
    recentActivity: Array<{
      id: string; title: string; status: string; created_at: string; updated_at: string
      channel_name: string; project_title: string
    }>
  }>('/dashboard/stats'),
  costs: async () => apiFetch<{
    totalCost: number
    monthlyCost: number
    dailyUsage: Array<{ date: string; count: number; cost: number }>
  }>('/dashboard/costs'),
}

// ==================== SETTINGS API ====================

export const settingsApi = {
  listUsers: async () => apiFetch<Array<{
    id: string; email: string; name: string; tier: string; points: number; is_admin: number; created_at: string; last_login_at: string
  }>>('/users/list'),
  updateUserRole: async (userId: string, role: string) =>
    apiFetch(`/users/${userId}/role`, { method: 'PUT', body: JSON.stringify({ role }) }),
  getUsage: async () => apiFetch<{
    totalCost: number; monthlyCost: number; totalGenerations: number; monthlyGenerations: number
  }>('/users/me/usage'),
  getUserPoints: async (userId: string) =>
    apiFetch<{ id: string; email: string; name: string; points: number; tier: string }>(`/users/${userId}/points`),
  getUserPointHistory: async (userId: string, limit = 30, offset = 0) =>
    apiFetch<Array<{ id: string; user_id: string; amount: number; type: string; reason: string; balance_after: number; reference_id: string | null; created_at: string }>>(`/users/${userId}/point-history?limit=${limit}&offset=${offset}`),
  adminCharge: async (userId: string, amount: number, reason: string) =>
    apiFetch<{ userId: string; amount: number; newBalance: number }>(`/users/${userId}/charge`, { method: 'POST', body: JSON.stringify({ amount, reason }) }),
}

// ==================== REWRITE API ====================

export interface RewriteProject {
  id: string
  user_id: string
  title: string
  description?: string
  status: string
  created_at: string
  updated_at: string
  sourcesCount?: number
  contentsCount?: number
}

export interface RewriteSource {
  id: string
  project_id: string
  user_id: string
  type: string
  title?: string
  content: string
  analysis?: string
  created_at: string
  updated_at: string
}

export interface RewriteChannel {
  id: string
  name: string
  description?: string
  icon?: string
  structure: string | object
  tone_rules?: string | object
  is_system: number
  created_at: string
  updated_at: string
}

export interface RewriteGuideline {
  id: string
  channel_id: string
  user_id?: string
  title: string
  content: string
  type: string
  section_type?: string | null
  is_active: number
  created_at: string
  updated_at: string
}

export interface RewriteContent {
  id: string
  project_id: string
  source_id: string
  channel_id: string
  user_id: string
  title?: string
  sections: string | Array<{ type: string; title: string; body: string }>
  status: string
  created_at: string
  updated_at: string
  channel_name?: string
}

export interface RewriteContentVersion {
  id: string
  content_id: string
  version: number
  title?: string
  sections: string
  change_summary?: string
  created_at: string
}

export const rewriteApi = {
  projects: {
    list: async (options?: { status?: string; limit?: number; offset?: number }): Promise<ApiResponse<RewriteProject[]>> => {
      const params = new URLSearchParams()
      if (options?.status) params.set('status', options.status)
      if (options?.limit) params.set('limit', String(options.limit))
      if (options?.offset) params.set('offset', String(options.offset))
      return apiFetch<RewriteProject[]>(`/rewrite/projects?${params.toString()}`)
    },
    get: async (id: string): Promise<ApiResponse<RewriteProject>> => {
      return apiFetch<RewriteProject>(`/rewrite/projects/${id}`)
    },
    create: async (data: { title: string; description?: string }): Promise<ApiResponse<RewriteProject>> => {
      return apiFetch<RewriteProject>('/rewrite/projects', { method: 'POST', body: JSON.stringify(data) })
    },
    update: async (id: string, data: { title?: string; description?: string; status?: string }): Promise<ApiResponse<RewriteProject>> => {
      return apiFetch<RewriteProject>(`/rewrite/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) })
    },
    delete: async (id: string): Promise<ApiResponse<null>> => {
      return apiFetch<null>(`/rewrite/projects/${id}`, { method: 'DELETE' })
    },
  },

  sources: {
    list: async (projectId: string): Promise<ApiResponse<RewriteSource[]>> => {
      return apiFetch<RewriteSource[]>(`/rewrite/sources?projectId=${projectId}`)
    },
    get: async (id: string): Promise<ApiResponse<RewriteSource>> => {
      return apiFetch<RewriteSource>(`/rewrite/sources/${id}`)
    },
    create: async (data: { projectId: string; content: string; title?: string; type?: string }): Promise<ApiResponse<RewriteSource>> => {
      return apiFetch<RewriteSource>('/rewrite/sources', { method: 'POST', body: JSON.stringify(data) })
    },
    update: async (id: string, data: { title?: string; content?: string }): Promise<ApiResponse<RewriteSource>> => {
      return apiFetch<RewriteSource>(`/rewrite/sources/${id}`, { method: 'PUT', body: JSON.stringify(data) })
    },
    delete: async (id: string): Promise<ApiResponse<null>> => {
      return apiFetch<null>(`/rewrite/sources/${id}`, { method: 'DELETE' })
    },
    uploadImage: async (projectId: string, file: File, options?: { method?: 'ocr' | 'api'; model?: string }): Promise<ApiResponse<RewriteSource & { imageKey?: string }>> => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('projectId', projectId)
      if (options?.method) formData.append('method', options.method)
      if (options?.model) formData.append('model', options.model)
      return apiFetch<RewriteSource & { imageKey?: string }>('/rewrite/sources/upload-image', {
        method: 'POST',
        body: formData,
      })
    },
  },

  channels: {
    list: async (): Promise<ApiResponse<RewriteChannel[]>> => {
      return apiFetch<RewriteChannel[]>('/rewrite/channels')
    },
    get: async (id: string): Promise<ApiResponse<RewriteChannel & { guidelines?: RewriteGuideline[] }>> => {
      return apiFetch<RewriteChannel & { guidelines?: RewriteGuideline[] }>(`/rewrite/channels/${id}`)
    },
    create: async (data: {
      name: string; description?: string; icon?: string;
      structure: { sections: Array<{ type: string; title: string; maxLength?: number; toneOverrides?: { tone?: string; rules?: string[]; useEmoji?: boolean } }> };
      toneRules?: { tone: string; rules: string[] }
    }): Promise<ApiResponse<RewriteChannel>> => {
      return apiFetch<RewriteChannel>('/rewrite/channels', { method: 'POST', body: JSON.stringify(data) })
    },
    update: async (id: string, data: Record<string, unknown>): Promise<ApiResponse<RewriteChannel>> => {
      return apiFetch<RewriteChannel>(`/rewrite/channels/${id}`, { method: 'PUT', body: JSON.stringify(data) })
    },
    delete: async (id: string): Promise<ApiResponse<null>> => {
      return apiFetch<null>(`/rewrite/channels/${id}`, { method: 'DELETE' })
    },
    createGuideline: async (data: { channelId: string; title: string; content: string; type: 'style' | 'example' | 'constraint'; sectionType?: string | null }): Promise<ApiResponse<RewriteGuideline>> => {
      return apiFetch<RewriteGuideline>('/rewrite/channels/guidelines', { method: 'POST', body: JSON.stringify(data) })
    },
    updateGuideline: async (id: string, data: Record<string, unknown>): Promise<ApiResponse<RewriteGuideline>> => {
      return apiFetch<RewriteGuideline>(`/rewrite/channels/guidelines/${id}`, { method: 'PUT', body: JSON.stringify(data) })
    },
    deleteGuideline: async (id: string): Promise<ApiResponse<null>> => {
      return apiFetch<null>(`/rewrite/channels/guidelines/${id}`, { method: 'DELETE' })
    },
    learnFromImages: async (channelId: string, originalImage: File, revisedImage: File): Promise<ApiResponse<{
      pairId: string
      analysis: string
      suggestions: Array<{
        title: string
        content: string
        type: string
        sectionType: string | null
        confidence: string
        reason: string
      }>
      originalImageKey: string
      revisedImageKey: string
    }>> => {
      const formData = new FormData()
      formData.append('channelId', channelId)
      formData.append('originalImage', originalImage)
      formData.append('revisedImage', revisedImage)
      return apiFetch(`/rewrite/channels/guidelines/learn`, { method: 'POST', body: formData })
    },
  },

  contents: {
    list: async (options?: { projectId?: string; channelId?: string; limit?: number; offset?: number }): Promise<ApiResponse<RewriteContent[]>> => {
      const params = new URLSearchParams()
      if (options?.projectId) params.set('projectId', options.projectId)
      if (options?.channelId) params.set('channelId', options.channelId)
      if (options?.limit) params.set('limit', String(options.limit))
      if (options?.offset) params.set('offset', String(options.offset))
      return apiFetch<RewriteContent[]>(`/rewrite/contents?${params.toString()}`)
    },
    get: async (id: string): Promise<ApiResponse<RewriteContent & { versions?: RewriteContentVersion[] }>> => {
      return apiFetch<RewriteContent & { versions?: RewriteContentVersion[] }>(`/rewrite/contents/${id}`)
    },
    update: async (id: string, data: { title?: string; sections?: Array<{ type: string; title: string; body: string }>; status?: string }): Promise<ApiResponse<RewriteContent>> => {
      return apiFetch<RewriteContent>(`/rewrite/contents/${id}`, { method: 'PUT', body: JSON.stringify(data) })
    },
    delete: async (id: string): Promise<ApiResponse<null>> => {
      return apiFetch<null>(`/rewrite/contents/${id}`, { method: 'DELETE' })
    },
    restore: async (contentId: string, versionId: string): Promise<ApiResponse<RewriteContent>> => {
      return apiFetch<RewriteContent>(`/rewrite/contents/${contentId}/restore/${versionId}`, { method: 'POST' })
    },
    getVersion: async (contentId: string, versionId: string): Promise<ApiResponse<RewriteContentVersion>> => {
      return apiFetch<RewriteContentVersion>(`/rewrite/contents/${contentId}/versions/${versionId}`)
    },
  },

  generate: {
    models: async (): Promise<ApiResponse<Array<{ id: string; name: string; description: string }>>> => {
      return apiFetch<Array<{ id: string; name: string; description: string }>>('/rewrite/generate/models')
    },
    create: async (data: { sourceId: string; channelIds: string[]; projectId: string; model?: string; guidelineIds?: Record<string, string[]>; customGuidelines?: Record<string, string[]> }): Promise<ApiResponse<{ results: unknown[] }>> => {
      return apiFetch<{ results: unknown[] }>('/rewrite/generate', { method: 'POST', body: JSON.stringify(data) })
    },
    rewrite: async (data: { contentId: string; sectionIndex: number; instruction?: string; guidelineIds?: string[]; customGuidelines?: string[]; model?: string }): Promise<ApiResponse<{ sectionIndex: number; original: string; rewritten: string }>> => {
      return apiFetch<{ sectionIndex: number; original: string; rewritten: string }>('/rewrite/generate/rewrite', { method: 'POST', body: JSON.stringify(data) })
    },
  },

  extractionPrompts: {
    list: async (channelId?: string): Promise<ApiResponse<ExtractionPrompt[]>> => {
      const params = channelId ? `?channelId=${channelId}` : ''
      return apiFetch<ExtractionPrompt[]>(`/rewrite/extraction-prompts${params}`)
    },
    get: async (id: string): Promise<ApiResponse<ExtractionPrompt>> => {
      return apiFetch<ExtractionPrompt>(`/rewrite/extraction-prompts/${id}`)
    },
    create: async (data: {
      channelId: string
      name: string
      description?: string
      promptTemplate: string
      sectionMapping: Record<string, string>
      outputFormat?: 'json' | 'markdown' | 'plain'
      isDefault?: boolean
    }): Promise<ApiResponse<ExtractionPrompt>> => {
      return apiFetch<ExtractionPrompt>('/rewrite/extraction-prompts', { method: 'POST', body: JSON.stringify(data) })
    },
    update: async (id: string, data: {
      name?: string
      description?: string
      promptTemplate?: string
      sectionMapping?: Record<string, string>
      outputFormat?: 'json' | 'markdown' | 'plain'
      isDefault?: boolean
      isActive?: boolean
    }): Promise<ApiResponse<ExtractionPrompt>> => {
      return apiFetch<ExtractionPrompt>(`/rewrite/extraction-prompts/${id}`, { method: 'PUT', body: JSON.stringify(data) })
    },
    delete: async (id: string): Promise<ApiResponse<null>> => {
      return apiFetch<null>(`/rewrite/extraction-prompts/${id}`, { method: 'DELETE' })
    },
    autoGenerate: async (channelId: string, file: File, name?: string): Promise<ApiResponse<{ prompt: ExtractionPrompt; analysis: string }>> => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('channelId', channelId)
      if (name) formData.append('name', name)
      return apiFetch<{ prompt: ExtractionPrompt; analysis: string }>('/rewrite/extraction-prompts/auto-generate', {
        method: 'POST',
        body: formData,
      })
    },
    extract: async (promptId: string, file: File): Promise<ApiResponse<{ sections: Record<string, string>; rawText: string; promptId: string }>> => {
      const formData = new FormData()
      formData.append('file', file)
      return apiFetch<{ sections: Record<string, string>; rawText: string; promptId: string }>(`/rewrite/extraction-prompts/${promptId}/extract`, {
        method: 'POST',
        body: formData,
      })
    },
  },
}

// ==================== Extraction Prompts Types ====================

export interface ExtractionPrompt {
  id: string
  channel_id: string
  user_id: string
  name: string
  description?: string
  prompt_template: string
  section_mapping: string // JSON string
  output_format: 'json' | 'markdown' | 'plain'
  sample_image_key?: string
  sample_image_url?: string
  is_default: number
  is_active: number
  usage_count: number
  created_at: string
  updated_at: string
}
